import React, { useEffect, useState } from 'react';
import {
  Table, TableHead, TableBody, TableRow, TableCell, Paper,
  IconButton, Typography, Button, Chip
} from '@mui/material';
import { Box, CircularProgress } from '@mui/material';
import { getIcon } from '../../../utils/IconHandler';
import { permissionOptions, discountType, getDiscountTypeLabel } from '../../../utils/Enums';
import DiscountModal from './DiscountModal';
import { deleteDiscount, getAllDiscounts, pauseDiscount, resumeDiscount } from '../../../services/DiscountService';
import { useTranslation } from 'react-i18next';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { formatDateTime } from '../../../utils/DateUtils';
import TableRefreshButton from '../../../components/TableRefreshButton/TableRefreshButton';

const DiscountTable = ({ locationId }) => {
  const { t } = useTranslation();

  const [discounts, setDiscounts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDiscounts = async () => {
    try {
      setLoading(true);
      console.log('[DiscountTable] Obteniendo descuentos para locationId:', locationId);
      const data = await getAllDiscounts(locationId);
      console.log('[DiscountTable] Descuentos obtenidos:', data?.length || 0, 'descuentos');
      setDiscounts(data || []);
    } catch (e) {
      console.error('Error al obtener descuentos', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiscounts();
  }, [locationId]);

  const handleAdd = () => {
    setSelectedDiscount(null);
    setModalOpen(true);
  };

  const handleEdit = (discount) => {
    setSelectedDiscount(discount);
    setModalOpen(true);
  };

  const handleToggleActive = async (discount) => {
    try {
      const success = discount.isActive
        ? await pauseDiscount(discount.id)
        : await resumeDiscount(discount.id);
  
      if (success) {
        fetchDiscounts();
      }
    } catch (e) {
      console.error('Error al cambiar estado del descuento', e);
    }
  };

  const handleDelete = async (discount) => {
    const confirmed = window.confirm(`¿Eliminar el código de descuento "${discount.code}"?`);
    if (!confirmed) return;

    try {
      const success = await deleteDiscount(discount.id);
      if (success) {
        fetchDiscounts();
      }
    } catch (e) {
      console.error('Error al eliminar el descuento', e);
    }
  };

  const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
  const hasPermission = (permissionId) => userPermissions.includes(permissionId);
  const canUpdateDiscount = hasPermission(permissionOptions.find(p => p.label === 'UPDATE_DISCOUNT').id);
  const canDeleteDiscount = hasPermission(permissionOptions.find(p => p.label === 'DELETE_DISCOUNT').id);
  const showActions = canUpdateDiscount || canDeleteDiscount;

  return (
    <>
      <Paper sx={{ padding: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 1 }}>
          <TableRefreshButton loading={loading} onClick={fetchDiscounts} />
          {hasPermission(permissionOptions.find(p => p.label === 'CREATE_DISCOUNT').id) && (
            <Button
              variant="outlined"
              startIcon={getIcon('add')}
              onClick={handleAdd}
            >
              {t('add_discount')}
            </Button>
          )}
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 1200 }}>
            <Table stickyHeader aria-label="discounts table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '10%' }}>Código</TableCell>
                  <TableCell sx={{ width: '18%' }}>Descripción</TableCell>
                  <TableCell sx={{ width: '10%' }}>Tipo</TableCell>
                  <TableCell sx={{ width: '10%' }}>Porcentaje</TableCell>
                  <TableCell sx={{ width: '8%' }}>Estado</TableCell>
                  <TableCell sx={{ width: '8%' }}>Usos</TableCell>
                  <TableCell sx={{ width: '8%' }} align="center">Fecha inicio</TableCell>
                  <TableCell sx={{ width: '8%' }} align="center">Fecha fin</TableCell>
                  <TableCell sx={{ width: '8%' }} align="center">En web</TableCell>
                  {showActions && (
                    <TableCell sx={{ width: '10%' }} align="right">Acciones</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Box py={3} display="flex" justifyContent="center">
                        <CircularProgress size={32} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  discounts.length > 0 ? (
                    discounts.map(({ discount, percentageDetails, buyXPayYDetails, isAvailableNow }) => (
                      <TableRow key={discount.id}>
                        <TableCell>{discount.code}</TableCell>
                        <TableCell>{discount.description}</TableCell>
                        <TableCell><Chip label={getDiscountTypeLabel(discount.type)} size="small" /></TableCell>
                        <TableCell>
                          {discount.type === discountType.PERCENTAGE && percentageDetails != null
                            ? `${percentageDetails.percentage}%`
                            : discount.type === discountType.BUY_X_PAY_Y && buyXPayYDetails != null
                              ? `${buyXPayYDetails.buyQuantity} / ${buyXPayYDetails.payQuantity}`
                              : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={isAvailableNow ? 'Activo' : 'Inactivo'}
                            size="small"
                            color={isAvailableNow ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell>
                          {discount.usageCount} / {discount.maxUses ?? '∞'}
                        </TableCell>
                        <TableCell align="center">{formatDateTime(discount.startDate)}</TableCell>
                        <TableCell align="center">{formatDateTime(discount.endDate)}</TableCell>
                        <TableCell align="center">
                          {!!discount.validForWeb ? 'Sí' : 'No'}
                        </TableCell>
                          {showActions && (
                              <TableCell align="right">
                                {canUpdateDiscount && (
                                  <>
                                    <IconButton onClick={() => handleEdit({ discount, percentageDetails, buyXPayYDetails })}>
                                      {getIcon('settings')}
                                    </IconButton>
                                    <IconButton onClick={() => handleToggleActive(discount)}>
                                      {discount.isActive ? <PauseIcon /> : <PlayArrowIcon />}
                                    </IconButton>
                                  </>
                                )}
                                {canDeleteDiscount && (
                                  <IconButton color="error" onClick={() => handleDelete(discount)}>
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                )}
                              </TableCell>
                          )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Typography variant="body2" color="textSecondary">
                          {t('empty_discount')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </Paper>

      <DiscountModal
        locationId={locationId}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          fetchDiscounts();
          setModalOpen(false);
        }}
        discountToEdit={selectedDiscount}
      />
    </>
  );
};

export default DiscountTable;
